/**
 * ClientOnboardingQCDisposal Component
 * Collects QA/QC requirements and sample disposal preferences
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { OnboardingProgressBar } from '../../components/ui';
import { useBeforeUnload } from '../../hooks';
import publicTokenService from '../../services/publicTokenService';
import styles from './ClientOnboarding.module.css';

const ClientOnboardingQCDisposal = () => {
    const { token } = useParams();
    const navigate = useNavigate();
    
    const [formData, setFormData] = useState({
        projectName: '',
        qcDataReported: false,
        standardLowLevelLimits: false,
        specialReportingLimits: '',
        internalChainOfCustody: false,
        chainOfCustodyInstructions: '',
        returnSamples: false,
        returnSamplesAfter: '',
        holdUntilNotified: false,
        holdUntilNotifiedAfter: ''
    });

    const [errors, setErrors] = useState({});
    const [isSaved, setIsSaved] = useState(true);
    const [initialData, setInitialData] = useState(null);

    useEffect(() => {
        const loadSavedData = async () => {
            try {
                // Try to load from backend first
                const progress = await publicTokenService.getProgress(token);
                const actualProgress = progress.progress || progress;
                
                if (actualProgress && actualProgress.stepData && actualProgress.stepData.step6) {
                    setFormData(actualProgress.stepData.step6);
                    setInitialData(JSON.stringify(actualProgress.stepData.step6));
                    return;
                }
            } catch (error) {
                // Fallback to sessionStorage
            }
            
            // Fallback to sessionStorage
            const saved = sessionStorage.getItem(`onboarding_qc_disposal_${token}`);
            if (saved) {
                const parsedData = JSON.parse(saved);
                setFormData(parsedData);
                setInitialData(saved);
            } else {
                setInitialData(JSON.stringify(formData));
            }
        };
        
        loadSavedData();
    }, [token]);

    // Track unsaved changes
    useEffect(() => {
        if (initialData) {
            const hasChanges = JSON.stringify(formData) !== initialData;
            setIsSaved(!hasChanges);
        }
    }, [formData, initialData]);

    // Warn before leaving if unsaved changes
    useBeforeUnload(!isSaved, 'You have unsaved changes. Please save your progress before leaving.');

    const handleCheckboxChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        
        // Clear related errors
        if (errors[field]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[field];
                return newErrors;
            });
        }
    };

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        
        if (errors[field]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[field];
                return newErrors;
            });
        }
    };

    const validateForm = () => {
        const newErrors = {};

        // Validate return samples timing if selected
        if (formData.returnSamples && !formData.returnSamplesAfter.trim()) {
            newErrors.returnSamplesAfter = 'Please specify how long after';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSaveAndExit = async () => {
        try {
            // Save to backend (step 6: QC & Disposal)
            await publicTokenService.saveProgress(token, {
                stepNumber: 6,
                stepData: formData,
                markStepComplete: false,
                moveToNextStep: false
            });
            
            // Save current data without validation
            sessionStorage.setItem(`onboarding_qc_disposal_${token}`, JSON.stringify(formData));
            setIsSaved(true);
            setInitialData(JSON.stringify(formData));
            alert('Your progress has been saved. You can return using the same link to continue.');
        } catch (error) {
            console.error('Failed to save progress:', error);
            alert('Failed to save progress. Please try again.');
        }
    };

    const handleContinue = async () => {
        if (validateForm()) {
            try {
                // Save to backend and mark step complete (step 6: QC & Disposal)
                await publicTokenService.saveProgress(token, {
                    stepNumber: 6,
                    stepData: formData,
                    markStepComplete: true,
                    moveToNextStep: true
                });
                
                // Save to session storage
                sessionStorage.setItem(`onboarding_qc_disposal_${token}`, JSON.stringify(formData));
                setIsSaved(true);
                
                // Navigate to review
                navigate(`/client-onboarding/${token}/review`);
            } catch (error) {
                console.error('Failed to save progress:', error);
                alert('Failed to save. Please try again.');
            }
        } else {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const handleGoBack = () => {
        navigate(`/client-onboarding/${token}/reporting-requirements`);
    };

    return (
        <React.Fragment>
            <OnboardingProgressBar />
            <div className={styles.container}>
                <div className={styles.header}>
                    <div className={styles.logoHeader}>
                        <img 
                            src="/Alt Logo Flat.png" 
                            alt="WETLAB" 
                            className={styles.logoHeaderImg}
                        />
                    </div>
                </div>

                <div className={styles.content}>
                    <div className={styles.formContainer}>
                        <div className={styles.headerSection}>
                            <h1 className={styles.title}>QA/QC Requirements & Sample Disposal</h1>
                            <p className={styles.subtitle}>
                                Please specify your quality control and sample handling preferences
                            </p>
                        </div>

                        <form className={styles.form}>
                            {/* QA/QC Requirements */}
                            <div className={styles.section}>
                                <h3 className={styles.sectionTitle}>QA/QC Requirements</h3>
                                
                                <div className={styles.questionGroup}>
                                    <label className={styles.questionLabel}>
                                        Do you have specific reporting limit requirements?
                                    </label>
                                    <div className={styles.radioGroup}>
                                        <label className={styles.radioLabel}>
                                            <input
                                                type="radio"
                                                name="standardLowLevelLimits"
                                                checked={formData.standardLowLevelLimits === true}
                                                onChange={() => handleCheckboxChange('standardLowLevelLimits', true)}
                                                className={styles.radio}
                                            />
                                            <span>Yes</span>
                                        </label>
                                        <label className={styles.radioLabel}>
                                            <input
                                                type="radio"
                                                name="standardLowLevelLimits"
                                                checked={formData.standardLowLevelLimits === false}
                                                onChange={() => handleCheckboxChange('standardLowLevelLimits', false)}
                                                className={styles.radio}
                                            />
                                            <span>No</span>
                                        </label>
                                    </div>
                                </div>

                                {formData.standardLowLevelLimits === true && (
                                    <div className={styles.formGroup}>
                                        <label className={styles.label}>Please specify your reporting limit requirements:</label>
                                        <textarea
                                            value={formData.specialReportingLimits}
                                            onChange={(e) => handleInputChange('specialReportingLimits', e.target.value)}
                                            className={styles.textarea}
                                            placeholder="Specify your reporting limit requirements..."
                                            rows="3"
                                        />
                                    </div>
                                )}
                            </div>

                            {/* Sample Disposal */}
                            <div className={styles.section}>
                                <h3 className={styles.sectionTitle}>Sample Disposal</h3>
                                <p className={styles.disposalNotice}>
                                    All samples are disposed of after 90 days unless specified by the client.
                                </p>

                                <div className={styles.questionGroup}>
                                    <label className={styles.questionLabel}>
                                        Samples should be returned to client?
                                    </label>
                                    <div className={styles.radioGroup}>
                                        <label className={styles.radioLabel}>
                                            <input
                                                type="radio"
                                                name="returnSamples"
                                                checked={formData.returnSamples === true}
                                                onChange={() => handleCheckboxChange('returnSamples', true)}
                                                className={styles.radio}
                                            />
                                            <span>Yes</span>
                                        </label>
                                        <label className={styles.radioLabel}>
                                            <input
                                                type="radio"
                                                name="returnSamples"
                                                checked={formData.returnSamples === false}
                                                onChange={() => handleCheckboxChange('returnSamples', false)}
                                                className={styles.radio}
                                            />
                                            <span>No</span>
                                        </label>
                                    </div>
                                </div>

                                {formData.returnSamples && (
                                    <div className={styles.formGroup}>
                                        <label className={styles.label}>
                                            If yes, after how long? <span className={styles.required}>*</span>
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.returnSamplesAfter}
                                            onChange={(e) => handleInputChange('returnSamplesAfter', e.target.value)}
                                            className={`${styles.input} ${errors.returnSamplesAfter ? styles.inputError : ''}`}
                                            placeholder="e.g., 30 days, 60 days"
                                        />
                                        {errors.returnSamplesAfter && (
                                            <span className={styles.errorText}>{errors.returnSamplesAfter}</span>
                                        )}
                                    </div>
                                )}

                                <div className={styles.questionGroup}>
                                    <label className={styles.questionLabel}>
                                        Hold Until Notified?
                                    </label>
                                    <div className={styles.radioGroup}>
                                        <label className={styles.radioLabel}>
                                            <input
                                                type="radio"
                                                name="holdUntilNotified"
                                                checked={formData.holdUntilNotified === true}
                                                onChange={() => handleCheckboxChange('holdUntilNotified', true)}
                                                className={styles.radio}
                                            />
                                            <span>Yes</span>
                                        </label>
                                        <label className={styles.radioLabel}>
                                            <input
                                                type="radio"
                                                name="holdUntilNotified"
                                                checked={formData.holdUntilNotified === false}
                                                onChange={() => handleCheckboxChange('holdUntilNotified', false)}
                                                className={styles.radio}
                                            />
                                            <span>No</span>
                                        </label>
                                    </div>
                                    {formData.holdUntilNotified === true && (
                                        <p className={styles.helpText} style={{ marginTop: '0.75rem', color: '#d97706', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                            <span style={{ fontSize: '1.125rem' }}>⚠️</span>
                                            <span><strong>Please Note:</strong> Charges will accrue for stored samples beyond the standard 90-day period.</span>
                                        </p>
                                    )}
                                </div>
                            </div>

                            {/* Action Buttons */}
                            <div className={styles.actions}>
                                <button
                                    type="button"
                                    onClick={handleGoBack}
                                    className={styles.backButton}
                                >
                                    ← Go Back
                                </button>
                                <div className={styles.rightActions}>
                                    <button
                                        type="button"
                                        onClick={handleSaveAndExit}
                                        className={styles.saveButton}
                                    >
                                        💾 Save and Continue Later
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleContinue}
                                        className={styles.continueButton}
                                    >
                                        Continue →
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>

            <div className={styles.footer}>
                <p className={styles.footerText}>
                    Questions? Contact us at <a href="mailto:support@wetlab.com">support@wetlab.com</a>
                </p>
            </div>
        </React.Fragment>
    );
};

export default ClientOnboardingQCDisposal;
